// ========================================================
// FAST HIT Website — Live Score Preview Component
// Real-time Firestore listener — shows actual live matches or
// a beautiful empty state encouraging users to start scoring
// ========================================================

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Activity, ArrowRight, MapPin, Radio, Plus, ChevronRight } from 'lucide-react';
import { subscribeLiveMatches } from '../../services/matchesService';
import type { Match } from '../../types';
import './LiveScorePreview.css';

export function LiveScorePreview() {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = subscribeLiveMatches((liveMatches) => {
      setMatches(liveMatches);
      setLoading(false);
    }, 4);
    return unsub;
  }, []);

  const hasMatches = matches.length > 0;

  return (
    <section className="live-preview" aria-labelledby="live-preview-title">
      <div className="live-preview__container">
        {/* Section Header */}
        <div className="live-preview__header">
          <div>
            <div className="section-eyebrow">
              <Activity size={14} className="text-cyan" />
              <span>Real-Time Scoreboard</span>
            </div>
            <h2 id="live-preview-title" className="section-title">
              Live Scores & Match Centers
            </h2>
            <p className="section-subtitle">
              Follow every boundary, wicket, and over delivery in real-time.
            </p>
          </div>

          <Link to="/live-scores" className="btn btn--outline btn--sm">
            <span>View All Scores</span>
            <ArrowRight size={14} />
          </Link>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="live-preview__grid">
            {[1, 2].map((i) => (
              <div key={i} className="live-preview__skeleton">
                <div className="skeleton skeleton--title" />
                <div className="skeleton skeleton--text" />
                <div className="skeleton skeleton--text" style={{ width: '60%' }} />
              </div>
            ))}
          </div>
        )}

        {/* Live Matches Grid */}
        {!loading && hasMatches && (
          <div className="live-preview__grid">
            {matches.map((match) => (
              <Link
                key={match.id}
                to={`/matches/${match.id}`}
                className="live-preview__card card"
              >
                {/* Card Header */}
                <div className="live-preview__card-header">
                  <span className="live-preview__status-pill">
                    <span className="live-preview__status-dot" />
                    {match.status === 'innings_break' ? 'INNINGS BREAK' : 'LIVE'}
                  </span>
                  <span className="live-preview__format-tag">
                    {match.format} {match.tournamentName ? `• ${match.tournamentName}` : ''}
                  </span>
                </div>

                {/* Teams & Scores */}
                <div className="live-preview__teams">
                  <div className="live-preview__team">
                    <div className="live-preview__team-meta">
                      <span className="live-preview__team-badge">
                        {match.teamA.shortName || match.teamA.name.slice(0, 3).toUpperCase()}
                      </span>
                      <span className="live-preview__team-name">{match.teamA.name}</span>
                    </div>
                    <div className="live-preview__score-val">
                      <span className="font-mono font-bold text-lg">{match.teamA.score || '0/0'}</span>
                      <span className="text-xs text-muted">({match.teamA.overs || '0.0'})</span>
                    </div>
                  </div>

                  <div className="live-preview__team">
                    <div className="live-preview__team-meta">
                      <span className="live-preview__team-badge live-preview__team-badge--alt">
                        {match.teamB.shortName || match.teamB.name.slice(0, 3).toUpperCase()}
                      </span>
                      <span className="live-preview__team-name">{match.teamB.name}</span>
                    </div>
                    <div className="live-preview__score-val">
                      <span className="font-mono font-bold text-lg">{match.teamB.score || '0/0'}</span>
                      <span className="text-xs text-muted">({match.teamB.overs || '0.0'})</span>
                    </div>
                  </div>
                </div>

                {/* Result / Equation */}
                {match.resultString && (
                  <div className="live-preview__equation">
                    {match.resultString}
                  </div>
                )}

                {/* Card Footer */}
                <div className="live-preview__card-footer">
                  <div className="live-preview__venue">
                    <MapPin size={12} className="text-muted" />
                    <span>{match.groundName}{match.city ? `, ${match.city}` : ''}</span>
                  </div>
                  <span className="live-preview__view-link">
                    <span>Scorecard</span>
                    <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Empty State — No live matches */}
        {!loading && !hasMatches && (
          <div className="live-preview__empty">
            <div className="live-preview__empty-icon">
              <Radio size={40} />
            </div>
            <h3 className="live-preview__empty-title">No Live Matches Right Now</h3>
            <p className="live-preview__empty-desc">
              When matches go live on FAST HIT, they'll appear here with real-time ball-by-ball updates.
              Start scoring a match or check back soon!
            </p>
            <div className="live-preview__empty-actions">
              <Link to="/app/scoring" className="btn btn--primary btn--sm btn--pill">
                <Plus size={14} />
                <span>Start Scoring</span>
              </Link>
              <Link to="/live-scores" className="btn btn--outline btn--sm btn--pill">
                <span>Browse Past Matches</span>
                <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
